import React, { useCallback, useEffect, useRef } from "react";

// Wave 3 — shared dialog primitive for FileCard confirmations (delete,
// resubmission). Consolidates the two formerly duplicated dialog blocks.
// Adds Escape-to-cancel + backdrop-click-to-cancel; focus is moved into the
// dialog on open and restored to the previously focused element on close.
const ModalDialog = ({
  open,
  onClose,
  labelledBy,
  role = "dialog",
  icon,
  iconTone,
  title,
  children,
  actions,
}) => {
  const dialogRef = useRef(null);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.key === "Escape" && onClose) {
        event.stopPropagation();
        onClose();
      }
    },
    [onClose]
  );

  const previouslyFocused = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    previouslyFocused.current = document.activeElement;
    const focusable = dialogRef.current?.querySelector(
      "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
    );
    focusable?.focus?.();
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      if (previouslyFocused.current?.focus) previouslyFocused.current.focus();
    };
  }, [open, handleKeyDown]);

  if (!open) return null;

  return (
    <div
      className="file-card__dialog-backdrop"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && onClose) onClose();
      }}
    >
      <section
        ref={dialogRef}
        className="file-card__dialog"
        role={role}
        aria-modal="true"
        aria-labelledby={labelledBy}
      >
        {icon && (
          <div
            className={`file-card__dialog-icon${iconTone ? ` file-card__dialog-icon--${iconTone}` : ""}`}
            aria-hidden="true"
          >
            {icon}
          </div>
        )}
        {title && <h4 id={labelledBy}>{title}</h4>}
        {children}
        {actions && <div className="file-card__dialog-actions">{actions}</div>}
      </section>
    </div>
  );
};

export default ModalDialog;
